import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme, TextField, InputAdornment, IconButton } from "@mui/material";
import { Close as CloseIcon, CalendarToday as CalendarIcon } from "@mui/icons-material";
import "./styles.css";

interface SysDateTimeProps {
  onChange?: (date: Date | null) => void;
  value?: Date | null;
  type?: "data" | "data_hora";
  placeholder?: string;
  disabled?: boolean;
  label?: string;
}

const SysDateTime: React.FC<SysDateTimeProps> = ({
  onChange,
  value,
  type = "data",
  placeholder,
  disabled = false,
  label
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(value || null);
  const [tempDate, setTempDate] = useState<Date>(() => {
    if (value) {
      return new Date(value.getFullYear(), value.getMonth(), 1);
    }
    return new Date();
  });
  const [selectedHour, setSelectedHour] = useState<string>(() => {
    if (value && type === "data_hora") {
      return String(value.getHours()).padStart(2, "0");
    }
    return "00";
  });
  const [selectedMinute, setSelectedMinute] = useState<string>(() => {
    if (value && type === "data_hora") {
      return String(value.getMinutes()).padStart(2, "0");
    }
    return "00";
  });
  const ref = useRef<HTMLDivElement>(null);
  const [portalPos, setPortalPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 0 });

  // Sincronizar valores quando a prop value mudar
  useEffect(() => {
    if (value !== undefined) {
      setSelectedDate(value);
      if (value) {
        setTempDate(new Date(value.getFullYear(), value.getMonth(), 1));
        if (type === "data_hora") {
          setSelectedHour(String(value.getHours()).padStart(2, "0"));
          setSelectedMinute(String(value.getMinutes()).padStart(2, "0"));
        }
      } else {
        setSelectedHour("00");
        setSelectedMinute("00");
      }
    }
  }, [value, type]);

  // Fechar ao clicar fora e ao redimensionar
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      const calendarEl = document.getElementById("sysdt-portal");
      const container = ref.current;
      if (
        container &&
        !container.contains(e.target as Node) &&
        calendarEl &&
        !calendarEl.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleResizeScroll = () => {
      if (isOpen) {
        updatePortalPosition();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("resize", handleResizeScroll);
    window.addEventListener("scroll", handleResizeScroll, true);

    // Usar requestAnimationFrame para atualizar suavemente durante o scroll
    let rafId: number | null = null;
    const handleScroll = () => {
      if (!isOpen) return;
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(() => {
        updatePortalPosition();
      });
    };

    window.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResizeScroll);
      window.removeEventListener("scroll", handleResizeScroll, true);
      window.removeEventListener("scroll", handleScroll, true);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [isOpen, type]);

  // Recalcular posição após o calendário ser renderizado
  useEffect(() => {
    if (isOpen) {
      // Pequeno delay para garantir que o DOM foi atualizado
      const timer = setTimeout(() => {
        updatePortalPosition();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isOpen, type, selectedDate]);

  const updatePortalPosition = () => {
    const container = ref.current;
    if (!container) return;
    const input = container.querySelector("input");
    if (!input) return;
    const rect = input.getBoundingClientRect();

    // Tentar calcular altura real do calendário se já estiver renderizado
    let calendarHeight = type === "data_hora" ? 450 : 380;
    const portalEl = document.getElementById("sysdt-portal");
    if (portalEl) {
      const calendarEl = portalEl.querySelector(".calendar");
      if (calendarEl) {
        calendarHeight = calendarEl.getBoundingClientRect().height + 20; // Adicionar margem
      }
    }

    // Calcular espaço disponível abaixo e acima
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const padding = 10; // Padding mínimo da tela
    const minSpace = padding + 8; // Espaço mínimo necessário

    // Usar getBoundingClientRect para posição relativa à viewport
    // Isso faz o calendário acompanhar o campo durante o scroll
    let top: number;
    let left = rect.left; // Não usar scrollX, usar posição relativa à viewport

    if (spaceBelow < calendarHeight + minSpace && spaceAbove > spaceBelow) {
      // Posicionar acima do campo (tem mais espaço acima)
      top = rect.top - calendarHeight - 8;
      // Se ainda assim sair da tela, ajustar para o topo
      if (top < padding) {
        top = padding;
      }
    } else if (spaceBelow < calendarHeight + minSpace) {
      // Não cabe nem acima nem abaixo bem, posicionar na melhor posição possível
      const spaceTotal = spaceBelow + spaceAbove;
      if (spaceTotal < calendarHeight) {
        // Não cabe completamente, centralizar o máximo possível
        top = rect.top - (calendarHeight - spaceBelow) / 2;
      } else {
        // Cabe, mas precisa ser ajustado
        top = rect.bottom + 8;
      }
      // Garantir que não saia da tela
      if (top < padding) {
        top = padding;
      }
      if (top + calendarHeight > window.innerHeight - padding) {
        top = window.innerHeight - calendarHeight - padding;
      }
    } else {
      // Posicionar abaixo do campo (padrão)
      top = rect.bottom + 8;
    }

    // Ajustar horizontalmente se o calendário sair da tela
    const calendarWidth = 320; // Largura estimada do calendário
    if (left + calendarWidth > window.innerWidth - padding) {
      left = window.innerWidth - calendarWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    setPortalPos({ top, left, width: rect.width });
  };

  const getDaysInMonth = (month: number, year: number) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const handleSelectDate = (newDate: Date) => {
    let finalDate = new Date(newDate);

    // Se tipo é data_hora, preservar hora e minuto
    if (type === "data_hora" && selectedDate) {
      finalDate.setHours(parseInt(selectedHour), parseInt(selectedMinute), 0, 0);
    } else if (type === "data_hora") {
      finalDate.setHours(parseInt(selectedHour), parseInt(selectedMinute), 0, 0);
    } else {
      finalDate.setHours(0, 0, 0, 0);
    }

    setSelectedDate(finalDate);

    // Se tipo é apenas data, fechar após selecionar
    if (type === "data") {
      setIsOpen(false);
      onChange?.(finalDate);
    } else {
      // Se tipo é data_hora, manter aberto para ajustar hora
      onChange?.(finalDate);
    }
  };

  const handleTimeChange = (hour: string, minute: string) => {
    setSelectedHour(hour);
    setSelectedMinute(minute);

    if (selectedDate) {
      const newDate = new Date(selectedDate);
      newDate.setHours(parseInt(hour), parseInt(minute), 0, 0);
      setSelectedDate(newDate);
      onChange?.(newDate);
    }
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";

    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    if (type === "data_hora") {
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hour}:${minute}`;
    }

    return `${day}/${month}/${year}`;
  };

  const isSameDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  };

  const renderMonthDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = getDaysInMonth(month, year);

    const days: JSX.Element[] = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${year}-${month}-${i}`} className="day empty"></div>);
    }

    for (let d = 1; d <= totalDays; d++) {
      const currentDate = new Date(year, month, d);
      const selected = isSameDay(currentDate, selectedDate);
      const isToday = isSameDay(currentDate, new Date());
      const isPast = currentDate < new Date() && !isSameDay(currentDate, new Date());

      days.push(
        <div
          key={`${year}-${month}-${d}`}
          className={`day ${selected ? "selected" : ""} ${isToday ? "today" : ""} ${isPast ? "past" : ""}`}
          onClick={() => handleSelectDate(currentDate)}
        >
          {d}
        </div>
      );
    }

    return days;
  };

  const generateHours = () => {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  };

  const generateMinutes = () => {
    return Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
  };

  const handleClear = () => {
    setSelectedDate(null);
    setSelectedHour("00");
    setSelectedMinute("00");
    onChange?.(null);
  };

  return (
    <div ref={ref} style={{ width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={formatDate(selectedDate)}
        placeholder={placeholder || (type === "data_hora" ? "Selecione data e hora" : "Selecione a data")}
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          const next = !isOpen;
          setIsOpen(next);
          if (next) {
            updatePortalPosition();
          }
        }}
        autoComplete="off"
        InputProps={{
          readOnly: true,
          style: { cursor: disabled ? "not-allowed" : "pointer" },
          endAdornment: (
            <InputAdornment position="end">
              {selectedDate && !disabled && (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClear();
                  }}
                  title="Limpar data"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
              <CalendarIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
        }}
      />

      {isOpen &&
        createPortal(
          <div
            id="sysdt-portal"
            className="calendar-portal"
            style={{
              top: `${portalPos.top}px`,
              left: `${portalPos.left}px`,
              position: 'fixed',
              zIndex: 9999
            }}
          >
            <div
              className="calendar"
              style={{
                backgroundColor: theme.palette.background.paper,
                borderColor: theme.palette.divider,
                color: theme.palette.text.primary
              }}
            >
              <div className="calendar-header">
                <button
                  style={{
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                  }}
                  onClick={() =>
                    setTempDate(
                      new Date(tempDate.getFullYear(), tempDate.getMonth() - 1, 1)
                    )
                  }
                >
                  ◀
                </button>
                <div className="calendar-title">
                  <span>
                    {tempDate.toLocaleString("pt-BR", { month: "long", year: "numeric" })}
                  </span>
                </div>
                <button
                  style={{
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent'
                  }}
                  onClick={() =>
                    setTempDate(
                      new Date(tempDate.getFullYear(), tempDate.getMonth() + 1, 1)
                    )
                  }
                >
                  ▶
                </button>
              </div>

              <div className="month">
                <div className="weekdays">
                  {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((d) => (
                    <div
                      key={`w-${d}`}
                      className="weekday"
                      style={{ color: theme.palette.text.secondary }}
                    >
                      {d}
                    </div>
                  ))}
                </div>
                <div className="days">{renderMonthDays(tempDate.getFullYear(), tempDate.getMonth())}</div>
              </div>

              {type === "data_hora" && (
                <div
                  className="time-selector"
                  style={{
                    borderTopColor: theme.palette.divider
                  }}
                >
                  <div
                    className="time-label"
                    style={{ color: theme.palette.text.primary }}
                  >
                    Hora:
                  </div>
                  <div className="time-inputs">
                    <select
                      value={selectedHour}
                      onChange={(e) => handleTimeChange(e.target.value, selectedMinute)}
                      className="time-select"
                      style={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider
                      }}
                    >
                      {generateHours().map((h) => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                    <span
                      className="time-separator"
                      style={{ color: theme.palette.text.primary }}
                    >
                      :
                    </span>
                    <select
                      value={selectedMinute}
                      onChange={(e) => handleTimeChange(selectedHour, e.target.value)}
                      className="time-select"
                      style={{
                        backgroundColor: theme.palette.background.paper,
                        color: theme.palette.text.primary,
                        borderColor: theme.palette.divider
                      }}
                    >
                      {generateMinutes().map((m) => (
                        <option key={m} value={m}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    className="confirm-btn"
                    onClick={() => setIsOpen(false)}
                  >
                    Confirmar
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default SysDateTime;

